import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCurrency } from '../../../../hooks/useCurrency';

const FinancialInformation = ({ formData, updateFormData, errors, currentUser, company }) => {
  const intl = useIntl();
  const { symbol, format: formatCurrencyValue } = useCurrency();

  const handleListPriceTypeChange = (type) => {
    if (type === 'tbd') {
      updateFormData({ 
        list_price_type: type,
        list_price: null
      });
    } else {
      updateFormData({ 
        list_price_type: type,
        list_price: formData.list_price || ''
      });
    }
  };

  const handleListPriceChange = (e) => {
    const value = e.target.value ? parseInt(e.target.value, 10) : null;
    updateFormData({ list_price: value });
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    return formatCurrencyValue(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // Check if list price should be hidden based on deal type and company settings
  const hideListPrice = 
    (formData.deal_type === 'continuity' && company?.hide_list_price_in_deal_room_wizard_continuity) ||
    (formData.deal_type === 'succession' && company?.hide_list_price_in_deal_room_wizard_succession) ||
    (formData.deal_type === 'sale' && company?.hide_list_price_in_deal_room_wizard_sale);

  if (hideListPrice) {
    return (
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">
            <FormattedMessage id="succession_deals.wizard.steps.financial_information.title" />
          </h2>
          <p className="mt-2 text-lg text-gray-600">
            <FormattedMessage 
              id="succession_deals.wizard.steps.financial_information.description_with_deal_type" 
              values={{ dealType: formData.deal_type }}
            />
          </p>
        </div>
        
        <div className="text-center py-8">
          <p className="text-gray-600">
            <FormattedMessage 
              id="succession_deals.wizard.steps.financial_information.hidden_pricing.not_required" 
              values={{ dealType: formData.deal_type }}
            />
          </p>
          <p className="text-sm text-gray-500 mt-2">
            <FormattedMessage id="succession_deals.wizard.steps.financial_information.hidden_pricing.proceed_next" />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">
          <FormattedMessage id="succession_deals.wizard.steps.financial_information.title" />
        </h2>
        <p className="mt-2 text-lg text-gray-600">
          <FormattedMessage id="succession_deals.wizard.steps.financial_information.description" />
        </p>
      </div>

      <div className="space-y-6">
        {/* List Price Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-4">
            <FormattedMessage id="succession_deals.wizard.steps.financial_information.list_price_label" />
          </label>
          
          <div className="space-y-4">
            {/* TBD Option */}
            <div className="flex items-center">
              <input
                id="list_price_tbd"
                name="list_price_type"
                type="radio"
                value="tbd"
                checked={formData.list_price_type === 'tbd' || (!formData.list_price_type && !formData.list_price && formData.list_price !== 0)}
                onChange={() => handleListPriceTypeChange('tbd')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <label htmlFor="list_price_tbd" className="ml-3 block text-sm font-medium text-gray-700">
                <FormattedMessage id="succession_deals.wizard.steps.financial_information.pricing_options.tbd" />
              </label>
            </div>

            {/* Custom Price Option */}
            <div className="flex items-center">
              <input
                id="list_price_custom"
                name="list_price_type"
                type="radio"
                value="custom"
                checked={formData.list_price_type === 'custom' || (formData.list_price || formData.list_price === 0)}
                onChange={() => handleListPriceTypeChange('custom')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <label htmlFor="list_price_custom" className="ml-3 block text-sm font-medium text-gray-700">
                <FormattedMessage id="succession_deals.wizard.steps.financial_information.pricing_options.custom" />
              </label>
            </div>

            {/* Custom Price Input */}
            {(formData.list_price_type === 'custom' || formData.list_price || formData.list_price === 0) && (
              <div className="ml-7 mt-3">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">{symbol}</span>
                  </div>
                  <input
                    type="number"
                    value={formData.list_price || ''}
                    onChange={handleListPriceChange}
                    className="block w-full pl-7 pr-12 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                    min="0"
                    step="1000"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  <FormattedMessage id="succession_deals.wizard.steps.financial_information.price_input_help" />
                </p>
              </div>
            )}
          </div>
          
          <div className="mt-3 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800">
              <FormattedMessage id="succession_deals.wizard.steps.financial_information.valuation_note" />
            </p>
          </div>
        </div>

        {/* Valuation Information */}
        {currentUser?.valuation_unlocked && (
          <div className="bg-gray-50 p-4 rounded-md">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              <FormattedMessage id="succession_deals.wizard.steps.financial_information.valuation_info.title" />
            </h3>
            <p className="text-sm text-gray-600">
              <FormattedMessage id="succession_deals.wizard.steps.financial_information.valuation_info.description" />
            </p>
            {currentUser?.dcf_valuation_unlocked && (
              <p className="text-sm text-gray-600 mt-1">
                <FormattedMessage id="succession_deals.wizard.steps.financial_information.valuation_info.dcf_available" />
              </p>
            )}
            {company?.valuation_url && (
              <a 
                href={company.valuation_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                <FormattedMessage id="succession_deals.wizard.steps.financial_information.valuation_info.access_tools" />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

FinancialInformation.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  errors: PropTypes.object,
  currentUser: PropTypes.object,
  company: PropTypes.object
};

export default FinancialInformation; 